import {
  Button,
  Checkbox,
  Flex,
  Tooltip,
  Typography,
  useToast,
} from '@flex-design-system/fx';
import { PlusIcon } from '@flex-design-system/legacy-flex-icons';
import { Translation, useTranslation } from '@flex-packages/i18n';
import { trans } from '@flex-packages/i18n-base';
import type { CandidateWorkExperienceBackground } from '@flex-private-packages/recruiting-common/base';
import { CandidateBackgroundList } from '@flex-private-packages/recruiting-common/components';
import { DndKit, useDndKitItem } from '@flex-services/shared-ui/layout';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  useController,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { getMobileStyle, mobileOnly, pcOnly } from '../../../../../utils';
import type { JobDescriptionFormState } from '../../../models';
import {
  BackgroundItemContentColumn,
  BackgroundItemContentRow,
  FormSection,
} from '../../../shared';
import type { BackgroundSectionHandle } from '../section-handle';

import {
  WorkExperienceCompanyNameAndStatusFormItem,
  WorkExperienceContractTypeSelectFormItem,
  WorkExperienceJobRoleInputFormItem,
  WorkExperienceJoinYearMonthInputFormItem,
  WorkExperienceRetirementYearMonthInputFormItem,
} from './form-items';
import Information from './Information';

const FIRST_ITEM_INDEX = 0;

export const WorkExperienceBackgroundSection = forwardRef<
  BackgroundSectionHandle,
  { required: boolean }
>(function WorkExperienceBackgroundSection({ required }, ref) {
  const [selectedItemIndex, setSelectedItemIndex] = useState<
    number | undefined
  >(0);

  const { control, setValue, trigger } =
    useFormContext<JobDescriptionFormState>();
  const noWorkExperience = useWatch({ control, name: 'noWorkExperience' });
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'workExperienceBackgrounds',
  });

  const itemRefs = useRef<Array<HTMLElement | null>>([]);

  useImperativeHandle(ref, () => {
    return {
      revealError: (itemIndex, shouldScroll) => {
        setSelectedItemIndex(itemIndex);
        requestAnimationFrame(() => {
          void trigger('workExperienceBackgrounds');
          if (shouldScroll) {
            itemRefs.current[itemIndex]?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        });
      },
    };
  });

  const handleChangeSelectedItemIndex = (newIndex: number) => {
    if (newIndex === selectedItemIndex) {
      setSelectedItemIndex(undefined);

      return;
    }

    setSelectedItemIndex(newIndex);
  };

  const handleAddEducationalBackgroundItem = () => {
    append({} as any);
    setSelectedItemIndex(fields.length);
  };

  const handleRemoveEducationalBackgroundItem = (targetIndex: number) => {
    remove(targetIndex);
  };

  return (
    <FormSection
      title={
        <FormSection.Title
          required={required}
          subtext={
            <FormSection.Subtext
              css={{
                ...getMobileStyle({
                  whiteSpace: 'pre-line',
                }),
              }}
            >
              <Translation tKey={trans('recruiting.work_experience.subtext')} />
            </FormSection.Subtext>
          }
          rightSlot={
            <Flex direction="row" align="center" css={{ gap: 16 }}>
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <Checkbox.Label>
                    <Checkbox
                      color="black"
                      checked={noWorkExperience}
                      onCheckedChange={(newChecked: boolean) =>
                        setValue('noWorkExperience', newChecked)
                      }
                    />
                    <Typography asChild>
                      <Translation
                        tKey={trans('recruiting.work_experience.empty')}
                      />
                    </Typography>
                  </Checkbox.Label>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Translation
                    tKey={trans('recruiting.work_experience.empty.description')}
                  />
                </Tooltip.Content>
              </Tooltip>

              <Button
                size="small"
                variant="outline"
                color="gray"
                leftSlot={<PlusIcon color="primary" />}
                onClick={handleAddEducationalBackgroundItem}
                css={pcOnly}
                disabled={noWorkExperience}
              >
                <Translation
                  tKey={trans('recruiting.background_section.add_button')}
                />
              </Button>
            </Flex>
          }
        >
          <Translation tKey={trans('recruiting.work_experience')} />
        </FormSection.Title>
      }
    >
      {!noWorkExperience && (
        <Flex direction="column" align="stretch" css={{ gap: 10 }}>
          <CandidateBackgroundList>
            <DndKit.Group
              items={fields}
              getIdentifier={field => field.id}
              onReorder={(_, oldIndex, newIndex) => {
                move(oldIndex, newIndex);
              }}
              dndContextProps={{
                onDragStart: () => {
                  setSelectedItemIndex(undefined);
                },
              }}
              axis="y"
            >
              {fields.map((value, currentIndex) => {
                return (
                  <ListItem
                    key={value.id}
                    id={value.id}
                    currentIndex={currentIndex}
                    itemRef={node => {
                      itemRefs.current[currentIndex] = node;
                    }}
                    required={required}
                    isExpanded={selectedItemIndex === currentIndex}
                    workExperienceBackgrounds={value}
                    handleChangeSelectedItemIndex={
                      handleChangeSelectedItemIndex
                    }
                    handleRemoveEducationalBackgroundItem={
                      handleRemoveEducationalBackgroundItem
                    }
                  />
                );
              })}
            </DndKit.Group>
          </CandidateBackgroundList>
          <Button
            size="large"
            variant="outline"
            color="gray"
            leftSlot={<PlusIcon color="primary" />}
            onClick={handleAddEducationalBackgroundItem}
            css={{ ...mobileOnly }}
          >
            <Translation
              tKey={trans('recruiting.background_section.add_button')}
            />
          </Button>
        </Flex>
      )}
    </FormSection>
  );
});

function ListItem({
  id,
  currentIndex,
  itemRef,
  required,
  workExperienceBackgrounds,
  isExpanded,
  handleChangeSelectedItemIndex,
  handleRemoveEducationalBackgroundItem,
}: {
  id: string;
  currentIndex: number;
  itemRef: (node: HTMLElement | null) => void;
  required: boolean;
  workExperienceBackgrounds: CandidateWorkExperienceBackground;
  isExpanded: boolean;
  handleChangeSelectedItemIndex: (newIndex: number) => void;
  handleRemoveEducationalBackgroundItem: (index: number) => void;
}) {
  const { t } = useTranslation();
  const { success } = useToast();

  const { control } = useFormContext<JobDescriptionFormState>();
  useController({
    control,
    name: `workExperienceBackgrounds.${currentIndex}`,
    rules: {
      validate: value => {
        if (!required) {
          return true;
        }
        // TODO: 각 Filed의 rule과 연동할 수 있는 방안 생각하기
        return Boolean(
          value.name &&
            value.workExperienceStatus &&
            value.contractType &&
            value.joinDate
        );
      },
    },
  });

  const { a11yAttributes, listeners, setNodeRef, style } = useDndKitItem({
    id,
  });

  return (
    <CandidateBackgroundList.Item
      {...a11yAttributes}
      ref={node => {
        setNodeRef(node);
        itemRef(node);
      }}
      expanded={isExpanded}
      style={style}
      onCollapse={() => handleChangeSelectedItemIndex(currentIndex)}
      header={
        <CandidateBackgroundList.ItemHeader
          leftSlot={
            <CandidateBackgroundList.ItemHeader.DragButton {...listeners} />
          }
          centerSlot={
            <Information
              companyName={workExperienceBackgrounds.name}
              workExperienceStatus={
                workExperienceBackgrounds.workExperienceStatus
              }
              isLastBackground={currentIndex === FIRST_ITEM_INDEX}
            />
          }
          rightSlot={
            <CandidateBackgroundList.ItemHeader.MoreDropdown
              deleteTitle={t(
                trans('recruiting.work_experience.delete.confirm.title')
              )}
              onItemDelete={() => {
                handleRemoveEducationalBackgroundItem(currentIndex);
                success(t(trans('recruiting.work_experience.delete.success')));
              }}
            />
          }
        />
      }
      content={
        <CandidateBackgroundList.ItemContent>
          <BackgroundItemContentColumn
            direction="column"
            align="stretch"
            css={{ gap: 24 }}
          >
            <WorkExperienceCompanyNameAndStatusFormItem
              currentIndex={currentIndex}
              required={required}
            />
            <BackgroundItemContentRow>
              <WorkExperienceContractTypeSelectFormItem
                currentIndex={currentIndex}
                required={required}
              />
              <WorkExperienceJobRoleInputFormItem currentIndex={currentIndex} />
            </BackgroundItemContentRow>
            <BackgroundItemContentRow>
              <WorkExperienceJoinYearMonthInputFormItem
                currentIndex={currentIndex}
                required={required}
              />
              <WorkExperienceRetirementYearMonthInputFormItem
                currentIndex={currentIndex}
                required={required}
              />
            </BackgroundItemContentRow>
          </BackgroundItemContentColumn>
        </CandidateBackgroundList.ItemContent>
      }
    />
  );
}
